// v7.56 — Creative Layers overlay: renders INSIDE the XYFlow ImageNode at tile
// size while a tile is in Layers mode. Healed base underneath, every cutout a
// draggable/resizable/rotatable Konva.Image on top, one Transformer bound to
// the selected layer. Mirrors CreativeEditNodeOverlay.tsx.
import { useEffect, useRef, useState } from 'react';
import type Konva from 'konva';
import { Stage, Layer, Image as KImage, Transformer } from 'react-konva';
import { useCreativeLayersStore, type LayerObject } from './creative-layers-store';

interface Props {
  imageId: string;
  width: number;
  height: number;
}

/** Load a bitmap with CORS enabled (Bunny serves CORS; the export needs it). */
function useLoadedImage(url: string | undefined): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) { setImg(null); return; }
    let alive = true;
    const el = new window.Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => { if (alive) setImg(el); };
    el.onerror = () => { if (alive) setImg(null); };
    el.src = url;
    return () => { alive = false; };
  }, [url]);
  return img;
}

function LayerImage({
  layer,
  register,
  onSelect,
  onChange,
}: {
  layer: LayerObject;
  register: (id: string, node: Konva.Image | null) => void;
  onSelect: (additive: boolean) => void;
  onChange: (patch: Partial<LayerObject>) => void;
}) {
  const img = useLoadedImage(layer.url);
  const bump = useCreativeLayersStore((s) => s.bumpImages);
  useEffect(() => { if (img) bump(); }, [img, bump]);
  if (!img) return null;
  return (
    <KImage
      ref={(node) => register(layer.id, node)}
      image={img}
      name="layer"
      x={layer.x}
      y={layer.y}
      width={layer.w}
      height={layer.h}
      rotation={layer.rotation ?? 0}
      draggable
      onClick={(e) => onSelect(!!(e.evt as MouseEvent).shiftKey || !!(e.evt as MouseEvent).metaKey)}
      onTap={() => onSelect(false)}
      onDragStart={() => onSelect(false)}
      onDragEnd={(e) => onChange({ x: Math.round(e.target.x()), y: Math.round(e.target.y()) })}
      onTransformEnd={(e) => {
        const node = e.target as Konva.Image;
        onChange({
          x: Math.round(node.x()),
          y: Math.round(node.y()),
          w: Math.max(8, Math.round(node.width() * node.scaleX())),
          h: Math.max(8, Math.round(node.height() * node.scaleY())),
          rotation: Math.round(node.rotation()),
        });
        node.scaleX(1);
        node.scaleY(1);
      }}
    />
  );
}

export function CreativeLayersNodeOverlay({ imageId, width, height }: Props) {
  const prepare = useCreativeLayersStore((s) => s.prepares[imageId]);
  const isActive = useCreativeLayersStore((s) => s.activeImageId === imageId);
  const selectedLayerId = useCreativeLayersStore((s) => s.selectedLayerId);
  const imagesVersion = useCreativeLayersStore((s) => s.imagesVersion);
  const { setStage, setSelected, toggleSelected, updateLayer, close, dismiss } = useCreativeLayersStore.getState();

  const stageRef = useRef<Konva.Stage>(null);
  const trRef = useRef<Konva.Transformer>(null);
  const nodeRefs = useRef<Map<string, Konva.Image>>(new Map());

  const doc = prepare?.doc ?? null;
  const scale = doc ? width / doc.width : 1;
  // Stage height follows the DOC aspect anchored to the tile width — tile
  // display dims can drift a few px off the true ratio.
  const stageH = doc ? Math.round(width * (doc.height / doc.width)) : height;
  const base = useLoadedImage(doc?.baseUrl);

  useEffect(() => {
    if (!isActive) return;
    setStage(stageRef.current);
    return () => setStage(null);
  }, [isActive, setStage, doc]);

  // Bind the Transformer to the selected layer (rebinding when its bitmap
  // arrives — LayerImage renders nothing until then).
  useEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    const node = selectedLayerId ? nodeRefs.current.get(selectedLayerId) : undefined;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [selectedLayerId, imagesVersion, doc]);

  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      const s = useCreativeLayersStore.getState();
      if (s.activeImageId !== imageId) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        void import('./canvas-store').then((m) => m.useCanvasStore.getState().closeSidebarPanel());
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && s.selectedLayerIds.length > 0) {
        const target = e.target as HTMLElement | null;
        if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
        e.stopPropagation();
        for (const id of s.selectedLayerIds) s.deleteLayer(id);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isActive, imageId, close]);

  if (!prepare) return null;

  if (prepare.phase === 'preparing') {
    return (
      <div className="nodrag" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#F5F3EE' }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', border: '2.5px solid #D3CDC2', borderTopColor: '#8A8377', animation: 'imgnode-spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 11, color: '#8A8377', fontWeight: 500, textAlign: 'center', padding: '0 12px' }}>{prepare.phaseNote}</div>
        <style>{`@keyframes imgnode-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (prepare.phase === 'failed') {
    return (
      <div className="nodrag" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#F5F3EE', padding: 12 }}>
        <div style={{ fontSize: 11, color: '#EF4444', textAlign: 'center' }}>{prepare.error}</div>
        <button
          className="nodrag"
          onClick={() => dismiss(imageId)}
          style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E2DC', background: '#fff', cursor: 'pointer', color: '#1A1A1A' }}
        >
          Close
        </button>
      </div>
    );
  }

  if (!isActive || !doc) return null;

  const ordered = [...doc.layers].sort((a, b) => a.z - b.z);

  return (
    <div
      className="nodrag"
      data-testid={`creative-layers-overlay-${imageId}`}
      style={{ position: 'relative', width: '100%', height: '100%', cursor: 'default' }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <Stage
        ref={stageRef}
        width={width}
        height={stageH}
        scaleX={scale}
        scaleY={scale}
        onMouseDown={(e) => {
          if (e.target === e.target.getStage() || e.target.name() === 'base') setSelected(null);
        }}
      >
        <Layer>
          {base && <KImage image={base} name="base" width={doc.width} height={doc.height} />}
          {ordered.map((l) => (
            <LayerImage
              key={l.id}
              layer={l}
              register={(id, node) => { if (node) nodeRefs.current.set(id, node); else nodeRefs.current.delete(id); }}
              onSelect={(additive) => { if (additive) toggleSelected(l.id); else if (!useCreativeLayersStore.getState().selectedLayerIds.includes(l.id)) setSelected(l.id); }}
              onChange={(patch) => updateLayer(l.id, patch)}
            />
          ))}
          <Transformer
            ref={trRef}
            keepRatio
            rotateEnabled
            enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
            borderStroke="#6366F1"
            anchorStroke="#6366F1"
            anchorFill="#ffffff"
            anchorSize={7}
            boundBoxFunc={(oldBox, newBox) => (newBox.width < 8 || newBox.height < 8 ? oldBox : newBox)}
          />
        </Layer>
      </Stage>
    </div>
  );
}
